import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  PlayerCardTemplate,
  PlayerCardTemplateDefault,
  PlayerCardServices,
  Page,
  PlayerCardDefaultables,
} from "../../../services/bedrock";
import { ToastContextData } from "./ToastContext";
import { PlayerCardsContextData } from "./PlayerCardsContext";
import { UserContextData } from "./UserContext";
import { CancelToken } from "axios";
import { PaginatedDropdownItemProps } from "../../../components/PaginatedDropdown";

export interface TemplatesContextData {
  templates: PlayerCardTemplate[];
  cachedTemplates: { [id: number]: PlayerCardTemplate };
  isLoading: boolean;
  activePage: number;
  totalPages: number;
  selectedTemplate: PlayerCardTemplate | undefined;
  setSelectedTemplate: React.Dispatch<React.SetStateAction<PlayerCardTemplate | undefined>>;
  inEditMode: boolean;
  setInEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  loadPage: (page?: number) => void;
  createTemplate: (name: string, duplicateTemplateId?: number) => Promise<PlayerCardTemplate>;
  deleteTemplate: (templateId: number) => Promise<void>;
  createDefault: (templateDefaultId: number, segmentationDetailId?: number) => Promise<PlayerCardTemplateDefault>;
  updateDefault: (
    templateDefaultId: number,
    templateId: number | null,
    segmentationDetailId?: number
  ) => Promise<PlayerCardTemplateDefault>;
  deleteDefault: (defaultId: number) => Promise<void>;
  handleTemplateUpdated: (template: PlayerCardTemplate) => void;
  loadTemplateDropdownOptions: (
    page: number,
    searchQuery?: string,
    cancelToken?: CancelToken,
    currentRecord?: PlayerCardTemplate | null,
    defaultRecord?: PlayerCardTemplate | null
  ) => Promise<Page<PaginatedDropdownItemProps>>;
}

export const InitialTemplatesContextData: TemplatesContextData = {
  templates: [],
  cachedTemplates: {},
  isLoading: true,
  activePage: 1,
  totalPages: 1,
  selectedTemplate: undefined,
  setSelectedTemplate: () => {},
  inEditMode: false,
  setInEditMode: () => {},
  loadPage: (_?: number) => {},
  createTemplate: (_: string, __?: number) => Promise.reject("Not implemented"),
  deleteTemplate: (_: number) => Promise.reject("Not implemented"),
  createDefault: (_: number, __?: number) => Promise.reject("Not implemented"),
  updateDefault: (_: number, __: number | null, ___?: number) => Promise.reject("Not implemented"),
  deleteDefault: (_: number) => Promise.reject("Not implemented"),
  handleTemplateUpdated: (_: PlayerCardTemplate) => {},
  loadTemplateDropdownOptions: (_: number, __?: string, ___?: CancelToken) => Promise.reject("Not implemented"),
};

export const TemplatesContext = (
  Toast: ToastContextData,
  PlayerCards: PlayerCardsContextData,
  User: UserContextData
): TemplatesContextData => {
  const [templates, setTemplates] = useState<PlayerCardTemplate[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activePage, setActivePage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [selectedTemplate, setSelectedTemplate] = useState<PlayerCardTemplate>();
  const [inEditMode, setInEditMode] = useState(false);
  const [cachedTemplates, setCachedTemplates] = useState<{ [id: number]: PlayerCardTemplate }>({});

  const updateCachedTemplates = (templates: PlayerCardTemplate[]) => {
    setCachedTemplates(prev => {
      const newTemplates = { ...prev };
      templates.forEach(template => {
        newTemplates[template.id] = template;
      });
      return newTemplates;
    });
  };

  const loadPage = useCallback(
    (page?: number) => {
      if (page !== undefined) setActivePage(page);
      else page = activePage;

      setIsLoading(true);
      PlayerCardServices.getTemplates(undefined, page)
        .then(response => {
          updateCachedTemplates(response.content);
          setTemplates(response.content);
          setTotalPages(response.pagination.totalPages);
          setSelectedTemplate(prev => {
            if (prev) {
              const index = response.content.findIndex(template => template.id === prev.id);
              if (index !== -1) {
                return response.content[index];
              }
            }
            return prev;
          });
        })
        .catch(console.error)
        .finally(() => setIsLoading(false));
    },
    [activePage]
  );
  if (User.isCoach) {
    useEffect(loadPage, []);
  }

  const createTemplate = async (templateName: string, duplicateTemplateId?: number) => {
    try {
      setIsLoading(true);
      const template = await PlayerCardServices.createTemplate(templateName, duplicateTemplateId);
      Toast.showSuccess("Template created successfully!");
      loadPage();
      setSelectedTemplate(template);
      setInEditMode(true);
      return template;
    } catch (e) {
      Toast.showError(e, "Failed to create template");
      throw e;
    }
  };

  const deleteTemplate = async (templateId: number) => {
    try {
      setIsLoading(true);
      await PlayerCardServices.deleteTemplate(templateId);
      Toast.showSuccess("Template deleted!");
      loadPage();
    } catch (e) {
      Toast.showError(e, "Failed to delete template");
      throw e;
    }
  };

  const createDefault = async (templateDefaultId: number, segmentationDetailId?: number) => {
    try {
      const templateDefault = await PlayerCardServices.createTemplateDefault(templateDefaultId, segmentationDetailId);
      Toast.showSuccess("Template default created!");
      return templateDefault;
    } catch (e) {
      Toast.showError(e, "Failed to create template default");
      throw e;
    }
  };

  const updateDefault = async (templateDefaultId: number, templateId: number | null, segmentationDetailId?: number) => {
    try {
      const templateDefault = await PlayerCardServices.updateTemplateDefault(
        templateDefaultId,
        templateId,
        segmentationDetailId
      );
      Toast.showSuccess("Template default updated!");
      return templateDefault;
    } catch (e) {
      Toast.showError(e, "Failed to update template default");
      throw e;
    }
  };

  const deleteDefault = async (defaultId: number) => {
    try {
      await PlayerCardServices.deleteTemplateDefault(defaultId);
      Toast.showSuccess("Template default deleted!");
    } catch (e) {
      Toast.showError(e, "Failed to delete template default");
      throw e;
    }
  };

  const handleTemplateUpdated = (template: PlayerCardTemplate) => {
    PlayerCards.handleTemplateUpdated(template);
    setSelectedTemplate(prev => {
      if (prev?.id === template.id) {
        return template;
      } else {
        return prev;
      }
    });
    setTemplates(prev => {
      const index = prev.findIndex(t => t.id === template.id);
      if (index >= 0) {
        return [...prev.slice(0, index), template, ...prev.slice(index + 1)];
      } else {
        return prev;
      }
    });
  };

  const convertToDropdownItems = (
    data: Page<PlayerCardTemplate>,
    page: number,
    searchQuery?: string,
    defaultRecord?: PlayerCardTemplate | null
  ) => {
    const content: PaginatedDropdownItemProps[] = data.content.map(item => {
      return {
        key: item.id,
        text: item.name,
        value: item.id,
      };
    });
    if (page === 1 && !searchQuery) {
      Object.values(PlayerCardDefaultables).forEach(defaultable => {
        var name = defaultable.name;
        if (defaultable.id === 0 && defaultRecord) name += ` (${defaultRecord.name})`;
        content.unshift({
          key: defaultable.id,
          text: <div className="customDropdownItem">{name}</div>,
          value: defaultable.id,
          searchValues: [],
        });
      });
    }
    return { ...data, content };
  };

  const loadTemplateDropdownOptions = async (
    page: number,
    searchQuery?: string,
    cancelToken?: CancelToken,
    currentRecord?: PlayerCardTemplate | null,
    defaultRecord?: PlayerCardTemplate | null
  ) => {
    const data = await PlayerCardServices.getTemplates(searchQuery, page, 25, cancelToken);
    updateCachedTemplates(data.content);
    if (currentRecord) data.content.push(currentRecord);
    return convertToDropdownItems(data, page, searchQuery, defaultRecord);
  };

  return {
    templates,
    cachedTemplates,
    isLoading,
    activePage,
    totalPages,
    selectedTemplate,
    setSelectedTemplate,
    inEditMode,
    setInEditMode,
    loadPage,
    createTemplate,
    deleteTemplate,
    createDefault,
    updateDefault,
    deleteDefault,
    handleTemplateUpdated,
    loadTemplateDropdownOptions,
  };
};
